/** Пикеры живых справочников для формы «Склады ФБС» (Э7).
 *
 *  Раньше ссылки 1С вводились строкой, и опечатка в GUID всплывала только на расчёте (а то и в
 *  документе 1С). Здесь человек выбирает ИМЯ — склад 1С, организацию, юрлицо кабинета, склад
 *  продавца на площадке, — а GUID/id подставляет портал и показывает серым, чтобы было видно,
 *  что именно уедет в конфиг.
 *
 *  Без молчаливых фоллбэков: источник недоступен → красная строка с текстом отказа бэка, а не
 *  пустой список (пустой выпадающий читается как «ничего не заведено»). Уже сохранённое
 *  значение, которого нет в свежем справочнике, не затирается и не прячется — оно остаётся
 *  выбранным с пометкой. Ручной ввод GUID оставлен за кнопкой: 1С падает, а работа стоять
 *  не должна.
 */
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ChevronDown, Search } from "lucide-react";
import React, { useState } from "react";

import * as api from "@/api/supply";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { extractErrorMessage } from "@/lib/api-errors";
import { cn } from "@/lib/utils";

import { resolveMpWarehouseName } from "./fbsWarehouseDraft";

const SELECT_CLS = "h-9 w-full rounded-md border bg-background px-2 text-sm";

function RefError({ error, what }: { error: unknown; what: string }): React.ReactElement {
  return (
    <span className="flex items-start gap-1 text-xs text-destructive">
      <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
      {extractErrorMessage(error, `Не удалось получить ${what}`)}
    </span>
  );
}

/** Поле «склад 1С»: имя из живого справочника + GUID серым; ручной ввод — за кнопкой. */
export function OnecWarehouseField({
  label,
  refKey,
  name,
  onPick,
}: {
  label: string;
  refKey: string;
  name: string;
  onPick: (v: { ref_key: string; name: string }) => void;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [term, setTerm] = useState("");
  const [manual, setManual] = useState(false);

  const list = useQuery({
    queryKey: ["supply", "fbs", "refs", "onec-warehouses", term],
    queryFn: () => api.getFbsOnecWarehouses(term),
    enabled: open,
    retry: false,
    staleTime: 60_000,
  });

  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs text-muted-foreground">{label}</span>
      <Popover
        open={open}
        onOpenChange={(o) => {
          setOpen(o);
          if (!o) {
            setQ("");
            setTerm("");
          }
        }}
      >
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="h-9 w-full justify-between font-normal"
          >
            <span className={cn("truncate", !name && "text-muted-foreground")}>
              {name || "выбрать склад 1С…"}
            </span>
            <ChevronDown className="size-4 shrink-0 opacity-60" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-2" align="start">
          <form
            className="mb-2 flex items-center gap-1"
            onSubmit={(e) => {
              e.preventDefault();
              setTerm(q.trim());
            }}
          >
            <Input
              autoFocus
              placeholder="часть названия склада…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="h-8"
            />
            <Button type="submit" size="icon" variant="outline" className="size-8 shrink-0">
              <Search className="size-4" />
            </Button>
          </form>
          {list.isFetching && (
            <div className="px-1 py-2 text-xs text-muted-foreground">Спрашиваем 1С…</div>
          )}
          {list.isError && (
            <div className="px-1 py-2">
              <RefError error={list.error} what="склады 1С" />
            </div>
          )}
          <div className="max-h-64 space-y-0.5 overflow-y-auto">
            {(list.data?.items ?? []).map((w) => (
              <button
                key={w.ref_key}
                type="button"
                className="w-full rounded px-2 py-1.5 text-left hover:bg-accent"
                onClick={() => {
                  onPick(w);
                  setOpen(false);
                }}
              >
                <div className="truncate text-sm">{w.name}</div>
                <div className="truncate font-mono text-[11px] text-muted-foreground">
                  {w.ref_key}
                </div>
              </button>
            ))}
            {list.data?.items.length === 0 && !list.isFetching && (
              <div className="px-1 py-2 text-xs text-muted-foreground">
                Складов с таким названием в 1С нет
              </div>
            )}
          </div>
          {list.data?.truncated && (
            <div className="px-1 pt-1 text-xs text-muted-foreground">
              Показаны первые {list.data.items.length} — уточните запрос
            </div>
          )}
        </PopoverContent>
      </Popover>

      {refKey && !manual && (
        <span className="truncate font-mono text-[11px] text-muted-foreground">{refKey}</span>
      )}
      {manual ? (
        <div className="space-y-1 rounded-md border p-2">
          <p className="text-[11px] text-muted-foreground">
            Ручной ввод — на случай, когда 1С недоступна: имя и GUID из карточки склада.
          </p>
          <Input
            value={name}
            placeholder="имя склада"
            onChange={(e) => onPick({ ref_key: refKey, name: e.target.value })}
            className="h-8"
          />
          <Input
            value={refKey}
            placeholder="GUID 1С"
            onChange={(e) => onPick({ ref_key: e.target.value.trim(), name })}
            className="h-8 font-mono text-xs"
          />
          <button
            type="button"
            className="text-[11px] underline text-muted-foreground"
            onClick={() => setManual(false)}
          >
            свернуть ручной ввод
          </button>
        </div>
      ) : (
        <button
          type="button"
          className="self-start text-[11px] underline text-muted-foreground"
          onClick={() => setManual(true)}
        >
          ввести GUID вручную
        </button>
      )}
    </div>
  );
}

/** Организация 1С — выпадающий список живого справочника. */
export function OrganizationSelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (refKey: string) => void;
}): React.ReactElement {
  const orgs = useQuery({
    queryKey: ["supply", "fbs", "refs", "organizations"],
    queryFn: api.getFbsOrganizations,
    retry: false,
    staleTime: 5 * 60_000,
  });
  const known = (orgs.data ?? []).some((o) => o.ref_key === value);

  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs text-muted-foreground">Организация (1С)</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={SELECT_CLS}
        disabled={orgs.isLoading}
      >
        <option value="">— выбрать организацию —</option>
        {(orgs.data ?? []).map((o) => (
          <option key={o.ref_key} value={o.ref_key}>
            {o.name}
          </option>
        ))}
        {value && !known && !orgs.isLoading && (
          // сохранённая организация, которой нет в свежем справочнике: не затираем молча
          <option value={value}>{value} (нет в справочнике 1С)</option>
        )}
      </select>
      {value && <span className="truncate font-mono text-[11px] text-muted-foreground">{value}</span>}
      {orgs.isError && <RefError error={orgs.error} what="организации 1С" />}
    </div>
  );
}

/** Юрлицо кабинета площадки — из Infisical (имена, без токенов). */
export function LegalSelect({
  mp,
  value,
  onChange,
}: {
  mp: api.FbsChannel["mp_code"];
  value: string;
  onChange: (legal: string) => void;
}): React.ReactElement {
  const legals = useQuery({
    queryKey: ["supply", "fbs", "refs", "legals", mp],
    queryFn: () => api.getFbsLegals(mp),
    retry: false,
    staleTime: 5 * 60_000,
  });
  const known = (legals.data ?? []).includes(value);

  return (
    <div className="min-w-48 flex-1">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={SELECT_CLS}
        disabled={legals.isLoading}
      >
        <option value="">— выбрать юрлицо —</option>
        {(legals.data ?? []).map((l) => (
          <option key={l} value={l}>
            {l}
          </option>
        ))}
        {value && !known && !legals.isLoading && (
          <option value={value}>{value} (нет в кабинетах)</option>
        )}
      </select>
      {legals.isError && <RefError error={legals.error} what="юрлица площадки" />}
    </div>
  );
}

/** Склад продавца НА площадке. Пусто — весь спрос юрлица. Читаем только у WB. */
export function MpWarehouseSelect({
  mp,
  legal,
  value,
  currentName,
  onChange,
}: {
  mp: api.FbsChannel["mp_code"];
  legal: string;
  value: number | null;
  currentName?: string | null;
  onChange: (w: { id: number; name: string } | null) => void;
}): React.ReactElement {
  const enabled = mp === "wb" && legal.trim().length > 0;
  const list = useQuery({
    queryKey: ["supply", "fbs", "refs", "mp-warehouses", mp, legal],
    queryFn: () => api.getFbsMpWarehouses(mp, legal),
    enabled,
    retry: false,
    staleTime: 5 * 60_000,
  });
  const known = (list.data ?? []).some((w) => w.id === value);

  if (mp !== "wb")
    return (
      <span className="min-w-48 flex-1 self-center text-xs text-muted-foreground">
        склад площадки — только WB
      </span>
    );

  return (
    <div className="min-w-48 flex-1">
      <select
        value={value ?? ""}
        onChange={(e) => {
          const id = e.target.value === "" ? null : Number(e.target.value);
          const name = resolveMpWarehouseName(id, { id: value, name: currentName ?? null }, list.data ?? []);
          onChange(id === null ? null : { id, name });
        }}
        className={SELECT_CLS}
        disabled={!enabled || list.isLoading}
      >
        <option value="">весь спрос юрлица</option>
        {(list.data ?? []).map((w) => (
          <option key={w.id} value={w.id}>
            {w.name}
          </option>
        ))}
        {value != null && !known && !list.isLoading && (
          <option value={value}>{value} (нет у площадки)</option>
        )}
      </select>
      {list.isError && <RefError error={list.error} what="склады продавца WB" />}
    </div>
  );
}
